import { useStore } from '@tanstack/react-form'

import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import { DiscountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useFinancialDocumentTotals } from '@/hooks/use-financial-document-totals'

type QuoteBuilderLiveTotalsValues = {
  items: QuoteLineItem[]
  discountType?: DiscountTypeEnum
  discountValue?: number
  enforcedTipPercentage?: number
  addonOffers: Array<{ unitPriceCents: number; quantity: number }>
}

type QuoteBuilderLiveTotalsOptions = {
  isReadOnly: boolean
  existingTotals?: {
    subtotalCents: number
    discountCents: number
    salesTaxCents: number
    enforcedTipCents?: number
    totalCents: number
  }
}

/**
 * Subscribe to quote-builder form fields that affect money, then recalculate.
 *
 * Reading `form.state.values` does not re-render this component, so Summary and
 * Preview would keep stale totals until some other state update (like Generate).
 *
 * TanStack Form's `form.store` is a Derived, not a Store. Accept the form object
 * structurally and let `useStore` handle the runtime subscription.
 */
export function useQuoteBuilderLiveTotals(
  form: { store: unknown },
  { isReadOnly, existingTotals }: QuoteBuilderLiveTotalsOptions,
) {
  const watchedFormValues = useStore(
    form.store as Parameters<typeof useStore>[0],
    (state) => {
      const values = (state as { values: QuoteBuilderLiveTotalsValues }).values
      return {
        items: values.items,
        discountType: values.discountType,
        discountValue: values.discountValue,
        enforcedTipPercentage: values.enforcedTipPercentage,
        addonOffers: values.addonOffers,
      }
    },
  )

  const totals = useFinancialDocumentTotals({
    items: watchedFormValues.items,
    discountType: watchedFormValues.discountType,
    discountValue: watchedFormValues.discountValue,
    enforcedTipPercentage: watchedFormValues.enforcedTipPercentage,
    existingTotals: isReadOnly ? existingTotals : undefined,
    isReadOnly,
  })

  return {
    ...totals,
    items: watchedFormValues.items,
    addonOffers: watchedFormValues.addonOffers,
  }
}
